/**
 * Displays log output, with integrated search.
 */

import {CopyButton, IconButton} from '@wandb/components';
import {Alert} from '@wandb/weave/components/Alert';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Alias} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {useLocalStorage} from '@wandb/weave/util/useLocalStorage';
import {AnsiUp as AU} from 'ansi_up';
import _ from 'lodash';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
  ListRowRenderer,
  OnScrollCallback,
} from 'react-virtualized';
import {twMerge} from 'tailwind-merge';

import {trackFileDownload} from '../../services/analytics/fileDownloadEvents';
import {
  LogsPageLocation,
  trackLogsPage,
} from '../../services/analytics/logsPageEvents';
import {DateFormat, format, isValidDate} from '../../util/date';
import {escapeRegexChars} from '../../util/regex';
import {run as runUrl} from '../../util/urls';
import {TabWaveLoader} from '../RunDetails/TabWaveLoader';
import {
  COMPACT_LINE_HEIGHT,
  DEFAULT_LINE_HEIGHT,
  MAX_VISIBLE_LINE_LENGTH,
  OUTER_PADDING,
  REGEX_RUN_ID,
  TRUNCATED_LABEL_MAX_CHARS,
} from './constants';
import {LogLabel} from './LogLabel';
import {
  labelToOption,
  LogLabelFilter,
  LogLabelFilterOption,
} from './LogLabelFilter';
import {LogLabelsBanner} from './LogLabelsBanner';
import {LogLimitReachedBanner} from './LogLimitReachedBanner';
import {LogSearchBrowser} from './LogSearchBrowser';
import {LogLineNode, QueryResultProps, withQuery} from './runLogQuery';
import {TimestampVisibilitySelect} from './TimestampVisibilitySelect';

interface LogProps extends QueryResultProps {
  outputLogFile: string | null;
  totalLines?: number;
  analyticsLocation?: LogsPageLocation;
}

type NumberedLogLine = LogLineNode & {lineNum?: number};

export const edgesToNumberedLines = (
  edges: Array<{node: LogLineNode}>,
  totalLines?: number
): NumberedLogLine[] => {
  if (!totalLines) {
    return edges.map(({node}) => node);
  }

  return edges.map(({node}, idx) => {
    return {
      ...node,
      lineNum: idx + 1 + Math.max(totalLines - edges.length, 0),
    };
  });
};

// Insert wrapper text at each location.
const wrapRanges = (
  text: string,
  indices: number[],
  length: number,
  start: (index: number) => string,
  end: (index: number) => string
) => {
  let result = '';
  let lastIndex = 0;
  for (const index of indices) {
    result += text.substring(lastIndex, index);
    const startText = start(index);
    const endText = end(index);
    result += startText + text.substring(index, index + length) + endText;
    lastIndex = index + length;
  }
  result += text.substring(lastIndex);
  return result;
};

const ANSI_BACKGROUND_YELLOW = '\u001b[48;5;11m';
const ANSI_BACKGROUND_ORANGE = '\u001b[48;5;220m';
const ANSI_RESET = '\u001b[0m';

// Convert the raw text of a log line to HTML.
// This handles ANSI color codes and also links run IDs to the run page.
const toHtml = (
  index: number,
  text: string,
  searchText: string,
  searchMatches: SearchMatches,
  searchActive: number
): string => {
  // We previously used a single instance of ansi_up but unterminated escape codes
  // caused rendering problems, so we now create a new instance for each line.
  const ansiUp = new AU();
  const lineMatches = searchMatches.indexed[index] ?? [];
  let activeIndex = -1;
  if (searchActive !== -1) {
    const active = searchMatches.ordered[searchActive];
    if (active.row === index) {
      activeIndex = active.col;
    }
  }
  // TODO: If the highlight starts within an existing colored section of log, the
  // reset code will incorrectly terminate the log's highlighting too. A solution
  // that can handle nested codes would be better but the effort doesn't seem justified now.
  const highlighted = wrapRanges(
    text,
    lineMatches,
    searchText.length,
    idx =>
      idx === activeIndex ? ANSI_BACKGROUND_ORANGE : ANSI_BACKGROUND_YELLOW,
    () => ANSI_RESET
  );

  const match = REGEX_RUN_ID.exec(highlighted);
  if (match == null) {
    return ansiUp.ansi_to_html(highlighted.replace(/ /g, '\u00a0'));
  }
  const [, projectName, entityName, name] = match;
  const urlParams = {entityName, projectName, name};
  const urlRun = runUrl(urlParams);
  const link = `<a href="${urlRun}">${name}</a>`;
  const parts = highlighted
    .split(name)
    .map(part => ansiUp.ansi_to_html(part.replace(/ /g, '\u00a0')));
  return parts.join(link);
};

// Starting position of a match. We don't support matching across lines or match patterns.
type SearchMatch = {
  row: number;
  col: number;
};

type SearchMatches = {
  // Helps us go to previous / next match and quickly get count.
  ordered: SearchMatch[];
  // Helps us with highlighting.
  indexed: Record<number, number[]>;
};

// exported for unit testing
export const LogInner = ({
  runLogQuery,
  totalLines,
  outputLogFile,
  analyticsLocation,
}: LogProps) => {
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [isTimestampVisible, setIsTimestampVisible] = useLocalStorage(
    'logTimestampsVisible',
    true
  );

  const allLines = useMemo(() => {
    if (runLogQuery.loading) {
      return [];
    }
    return edgesToNumberedLines(runLogQuery.logLines.edges, totalLines);
  }, [runLogQuery, totalLines]);

  const allLabels = useMemo(() => {
    const uniqLabels = _.uniq(allLines.map(line => line.label));
    if (uniqLabels.length === 1 && !uniqLabels[0]) {
      // none of the lines are labeled; return empty list
      return [];
    }
    // at least some lines are labeled; sort & return, including empty labels
    return uniqLabels.sort().map(labelToOption);
  }, [allLines]);

  // Filter lines by labels
  const [selectedLabels, setSelectedLabels] = useState<
    readonly LogLabelFilterOption[]
  >([]);
  const filteredLines = useMemo(() => {
    return selectedLabels.length
      ? allLines.filter(({label}) =>
          selectedLabels.some(({value}) => value === label)
        )
      : allLines;
  }, [allLines, selectedLabels]);
  const filteredLabels = selectedLabels.length ? selectedLabels : allLabels;

  const lineHeight =
    isTimestampVisible || filteredLabels.length > 0
      ? DEFAULT_LINE_HEIGHT
      : COMPACT_LINE_HEIGHT;
  const cache = useMemo(
    () =>
      new CellMeasurerCache({
        defaultHeight: lineHeight,
        fixedWidth: true,
      }),
    [lineHeight]
  );

  // For analytics; need to know if selected labels changed between opening/closing multi-select menu
  const [selectedLabelsOnMenuOpen, setSelectedLabelsOnMenuOpen] = useState<
    readonly LogLabelFilterOption[] | null
  >(null);

  // Match the user is viewing
  const [searchActive, setSearchActive] = useState(-1);
  const [searchMatches, setSearchMatches] = useState<SearchMatches>({
    ordered: [],
    indexed: {},
  });

  const [searchText, setSearchText] = useState('');

  const lastWidthRef = useRef(window.innerWidth);

  const listRef = useRef<List>(null);

  // used to determine list height based on where it is in the viewport
  const [listContainerTop, setListContainerTop] = useState<
    number | undefined
  >();

  const scrollTo = (row: number) => {
    const list = listRef.current;
    if (list) {
      list.forceUpdateGrid();
      list.scrollToRow(row);
    }
  };

  const onListScroll: OnScrollCallback = e => {
    // shouldScrollToBottom is used to scroll user to latest log on initial load.
    // once scrolled, set to false so we don't interfere with user scrolling.
    if (shouldScrollToBottom && e.scrollTop > 0) {
      setShouldScrollToBottom(false);
    }
  };

  const onSearchChange = (value: string) => {
    const matches: SearchMatches = {
      ordered: [],
      indexed: {},
    };
    if (value) {
      const regex = new RegExp(escapeRegexChars(value), 'ig');
      for (let i = 0; i < filteredLines.length; i++) {
        const text = filteredLines[i].line;
        for (const match of text.matchAll(regex)) {
          const {index} = match;
          if (index != null) {
            matches.ordered.push({row: i, col: index});
            if (i in matches.indexed) {
              matches.indexed[i].push(index);
            } else {
              matches.indexed[i] = [index];
            }
          }
        }
      }
    }
    setSearchText(value);
    setSearchMatches(matches);
    setSearchActive(matches.ordered.length ? 0 : -1);
    if (matches.ordered.length) {
      scrollTo(matches.ordered[0].row);
    }
  };

  // When displayed lines change, we need to refresh measurer & search states
  useEffect(() => {
    cache.clearAll();
    if (searchText) {
      onSearchChange(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLines]);

  const onSetIndex = (index: number) => {
    setSearchActive(index);
    scrollTo(searchMatches.ordered[index].row);
  };

  const onKeyDown = (key: string) => {
    if (key === 'Enter' && searchMatches.ordered.length) {
      onSetIndex((searchActive + 1) % searchMatches.ordered.length);
    }
  };

  // Find longest visible lineNum & label to help us align log contents
  const maxLineNumLength =
    _.last(filteredLines)?.lineNum?.toString().length ?? 0;
  const maxLabelLength = Math.min(
    Math.max(...filteredLabels.map(({displayText}) => displayText.length)),
    TRUNCATED_LABEL_MAX_CHARS + 1 // +1 for ellipsis character
  );

  const stripAnsiCodes = (text: string): string => {
    // eslint-disable-next-line no-control-regex
    return text.replace(/\u001b\[[0-9;]*m/g, '');
  };

  const formatLogsForCopy = useMemo(() => {
    return filteredLines
      .map(line => {
        const parts = [];

        if (isTimestampVisible && isValidDate(new Date(line.timestamp))) {
          parts.push(
            format(
              new Date(line.timestamp),
              DateFormat.YYYY_MM_DD_HH_MM_SS_DASHES
            )
          );
        }

        if (filteredLabels.length > 0 && line.label) {
          parts.push(`[${line.label}]`);
        }

        parts.push(stripAnsiCodes(line.line));

        return parts.join(' ');
      })
      .join('\n');
  }, [filteredLines, filteredLabels, isTimestampVisible]);

  const rowRenderer: ListRowRenderer = ({key, index, style, parent}) => {
    // Only called on valid keys, since we pass rowCount in to VirtualList
    const line = filteredLines[index];
    const {line: rawText, timestamp: rawTimestamp} = line;
    const timestamp = new Date(rawTimestamp);
    const text =
      rawText.length > MAX_VISIBLE_LINE_LENGTH
        ? rawText.substring(0, MAX_VISIBLE_LINE_LENGTH) +
          '[wandb: long log line truncated]'
        : rawText;

    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
        minHeight={lineHeight}>
        <div role="row" style={style} className="flex gap-8 px-16 py-2">
          {line.lineNum != null && (
            <span
              aria-label="line number"
              className="select-none text-moon-500">
              {`${line.lineNum}`.padStart(maxLineNumLength, '\u00a0')}
            </span>
          )}
          <div className="flex flex-wrap gap-8 md:flex-nowrap">
            {isTimestampVisible && isValidDate(timestamp) && (
              <span data-testid="timestamp-alias" className="shrink-0">
                <Alias
                  color="moon"
                  label={format(
                    timestamp,
                    DateFormat.YYYY_MM_DD_HH_MM_SS_DASHES
                  )}
                />
              </span>
            )}
            {filteredLabels.length > 0 && (
              <LogLabel
                label={line.label}
                maxLabelLength={maxLabelLength}
                truncatedMaxChars={TRUNCATED_LABEL_MAX_CHARS}
              />
            )}
            <span
              className="break-all dark:text-moon-800"
              dangerouslySetInnerHTML={{
                __html:
                  // The ansi to HTML library takes care of escaping.
                  // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
                  toHtml(index, text, searchText, searchMatches, searchActive) +
                  '<br />', // ensures that empty lines have a height
              }}
            />
          </div>
        </div>
      </CellMeasurer>
    );
  };

  if (runLogQuery.loading) {
    return <TabWaveLoader />;
  }

  const rowCount = filteredLines.length;
  // pass through lastLine to force update when it changes (progress bars)
  let lastLine = '';
  if (rowCount > 0) {
    lastLine = _.last(filteredLines)!.line;
  }

  // Because of useEffect, the query might be finished before "lines" is set.
  // To avoid a flashing appearance of this alert, we check for queryRowCount instead.
  const queryRowCount = runLogQuery.logLines.edges.length;
  if (queryRowCount === 0) {
    // Agents didn't used to save logs to a run. Runs might not have output.
    return (
      <div style={{margin: 10}}>
        <Alert severity="info">No log output available.</Alert>
      </div>
    );
  }

  const isStorageLimitReached = totalLines && totalLines > allLines.length;

  return (
    <Tailwind>
      <div className={`bg-white p-${OUTER_PADDING}`}>
        {isStorageLimitReached && (
          <LogLimitReachedBanner numLinesAvailable={allLines.length} />
        )}
        {allLabels.length > 0 && (
          <LogLabelsBanner analyticsLocation={analyticsLocation} />
        )}
        <div className="flex flex-wrap gap-16 md:flex-nowrap">
          <div className="mt-4 flex w-full flex-wrap gap-8 md:flex-nowrap">
            <div className="w-full md:w-auto">
              <TimestampVisibilitySelect
                isTimestampVisible={isTimestampVisible}
                onChange={value => {
                  setIsTimestampVisible(value);
                  if (analyticsLocation !== undefined) {
                    trackLogsPage({
                      action: 'Timestamp visibility toggled',
                      location: analyticsLocation,
                      isTimestampVisible: value,
                    });
                  }
                }}
              />
            </div>
            {allLabels.length > 0 && (
              <div className="w-full min-w-[200px] flex-1 md:w-[60%] md:max-w-[650px]">
                <LogLabelFilter
                  allLabels={allLabels}
                  selectedLabels={selectedLabels}
                  onChange={setSelectedLabels}
                  onMenuOpen={() => {
                    setSelectedLabelsOnMenuOpen(selectedLabels);
                  }}
                  onMenuClose={() => {
                    if (
                      analyticsLocation !== undefined &&
                      selectedLabels !== selectedLabelsOnMenuOpen
                    ) {
                      trackLogsPage({
                        action: 'Label filters applied',
                        location: analyticsLocation,
                        numLabelsSelected: selectedLabels.length,
                        isSearchActive: !!searchText,
                      });
                    }
                    setSelectedLabelsOnMenuOpen(null);
                  }}
                />
              </div>
            )}
            <div className="flex w-full gap-8 md:w-[40%]">
              <div className="flex-1 md:max-w-[300px]">
                <TextField
                  ariaLabel="Search logs"
                  icon="search"
                  placeholder="Search"
                  value={searchText}
                  onChange={onSearchChange}
                  onKeyDown={onKeyDown}
                />
              </div>
              {searchText && (
                <LogSearchBrowser
                  index={searchActive}
                  count={searchMatches.ordered.length}
                  onSetIndex={onSetIndex}
                />
              )}
            </div>
          </div>
          <div className="flex w-full gap-8 md:w-auto">
            <CopyButton content={formatLogsForCopy} size="md" />
            {outputLogFile && (
              <IconButton
                icon="download"
                variant="secondary"
                size="md"
                tooltip={{children: 'Download log file'}}
                aria-label="Download log file"
                href={outputLogFile}
                download="output.log"
                onClick={
                  analyticsLocation
                    ? () => {
                        trackFileDownload({
                          fileType: 'log',
                          isMultipartLogsFile: false,
                          location: analyticsLocation,
                        });
                      }
                    : undefined
                }
              />
            )}
          </div>
        </div>
        <div
          ref={node => {
            if (node) {
              setListContainerTop(node.getBoundingClientRect().top);
            }
          }}
          style={{
            // NOTE: cannot use tailwind for dynamic values
            height: listContainerTop
              ? `calc(100vh - ${listContainerTop + OUTER_PADDING}px)`
              : 0,
            lineHeight: `${lineHeight}px`,
          }}
          className={twMerge(
            'mt-16 overflow-auto bg-moon-50 py-16',
            'font-mono text-[13px]'
          )}>
          <AutoSizer>
            {({width, height}) => {
              if (width !== lastWidthRef.current) {
                // recompute row heights on window resize
                cache.clearAll();
                lastWidthRef.current = width;
              }
              return (
                <List
                  ref={listRef}
                  onScroll={onListScroll}
                  scrollToIndex={
                    shouldScrollToBottom ? filteredLines.length - 1 : undefined
                  }
                  scrollToAlignment="end"
                  height={height}
                  width={width}
                  containerRole="rowgroup"
                  rowCount={rowCount}
                  estimatedRowHeight={lineHeight}
                  rowHeight={cache.rowHeight}
                  rowRenderer={rowRenderer}
                  lastLine={lastLine}
                />
              );
            }}
          </AutoSizer>
        </div>
      </div>
    </Tailwind>
  );
};

export const Log = withQuery(LogInner);
